import { CONSTANTS } from './constants';
import { calculateScientificFrame as calculateFreeSpaceFrame, squeezingParameter, thermalOccupation } from './freeSpaceKernel';
import { clamp, gaussian, linspace, mulberry32, trapezoid } from './numerics';
import type { AppState, PlotSeries, ScientificFrame, ScientificMetric } from './types';

const { speedOfLight: C, planck: H, reducedPlanck: HBAR, fineStructureConstant: ALPHA, electronMass: ME, pi: PI } = CONSTANTS;
const TWO_PI = 2 * PI;

const clonedState = (state: Readonly<AppState>): AppState => structuredClone(state) as AppState;

const baseFrame = (state: Readonly<AppState>, animationTimeSeconds: number, frequencyHz?: number): ScientificFrame => {
  const draft = clonedState(state);
  draft.era = 'modern';
  draft.scientific.frequencyLog10 = Math.log10(Math.max(1, frequencyHz ?? 10 ** state.scientific.frequencyLog10));
  draft.scientific.detectorBandwidthLog10 = Math.min(
    draft.scientific.detectorBandwidthLog10,
    Math.log10(Math.max(1, (frequencyHz ?? 10 ** state.scientific.frequencyLog10) * 1.5))
  );
  draft.scientific.boundaryGeometry = 'none';
  return calculateFreeSpaceFrame(draft, animationTimeSeconds);
};

const replaceFieldSlice = (frame: ScientificFrame, builder: (x: number, y: number) => number): number[][] => {
  const rows = frame.fieldSlice.length || 32;
  const cols = frame.fieldSlice[0]?.length || 32;
  return Array.from({ length: rows }, (_, row) => Array.from({ length: cols }, (_, col) => {
    const x = cols <= 1 ? 0 : (col / (cols - 1)) * 2 - 1;
    const y = rows <= 1 ? 0 : (row / (rows - 1)) * 2 - 1;
    return builder(x, y);
  }));
};

const quadratureCloud = (
  seed: number,
  count: number,
  varianceX: number,
  varianceP: number,
  angleDegrees: number,
  meanX = 0,
  meanP = 0
): Array<{ x: number; y: number }> => {
  const random = mulberry32(seed);
  const theta = angleDegrees * PI / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  return Array.from({ length: count }, () => {
    const x0 = meanX + gaussian(random) * Math.sqrt(Math.max(varianceX, 1e-15));
    const p0 = meanP + gaussian(random) * Math.sqrt(Math.max(varianceP, 1e-15));
    return { x: x0 * cos - p0 * sin, y: x0 * sin + p0 * cos };
  });
};

const noisySeries = (seed: number, count: number, scale: number, offset = 0): PlotSeries[] => {
  const random = mulberry32(seed);
  return [{
    name: 'Detector samples',
    values: Array.from({ length: count }, (_, index) => ({ x: index, y: offset + gaussian(random) * scale })),
    xUnit: 'sample',
    yUnit: 'a.u.'
  }];
};

const metric = (label: string, value: number, unit: string, description?: string): ScientificMetric => ({ label, value, unit, description });

const normalizedLorentzian = (frequencyHz: number, centerHz: number, fwhmHz: number): number => {
  const half = Math.max(1e-30, fwhmHz / 2);
  return 1 / (1 + ((frequencyHz - centerHz) / half) ** 2);
};

export const calculateDceFrame = (state: Readonly<AppState>, animationTimeSeconds: number): ScientificFrame => {
  const driveHz = 10 ** state.scientific.modulationFrequencyLog10;
  const driveOmega = TWO_PI * driveHz;
  const phaseVelocity = C * clamp(state.scientific.phaseVelocityFraction, 0.05, 0.95);
  const effectiveLength = 10 ** state.scientific.effectiveLengthLog10;
  const deltaLength = effectiveLength * clamp(state.scientific.modulationAmplitude, 0, 0.45);
  const epsilon = deltaLength * driveOmega / (2 * phaseVelocity);
  const velocityRatio = deltaLength * driveOmega / phaseVelocity;
  const count = clamp(Math.round(state.scientific.numericalResolution * 5), 160, 800);
  const frequencies = linspace(Math.max(1, driveHz / count), driveHz * (1 - 1 / count), count);
  const dceOccupation = frequencies.map((frequency) => {
    const omega = TWO_PI * frequency;
    return (deltaLength / phaseVelocity) ** 2 * omega * Math.max(0, driveOmega - omega);
  });
  const thermalDirect = frequencies.map((frequency) => thermalOccupation(frequency, state.scientific.temperature));
  const thermalMixing = frequencies.map((frequency) => {
    const omega = TWO_PI * frequency;
    const partnerHz = Math.max(1, driveHz - frequency);
    return (deltaLength / phaseVelocity) ** 2 * omega * Math.abs(driveOmega - omega) * thermalOccupation(partnerHz, state.scientific.temperature);
  });
  const outputOccupation = frequencies.map((_, index) => (thermalDirect[index] ?? 0) + (thermalMixing[index] ?? 0) + (dceOccupation[index] ?? 0));
  const photonRate = trapezoid(frequencies, dceOccupation);
  const thermalRate = trapezoid(frequencies, thermalDirect);
  const radiatedPower = trapezoid(frequencies, frequencies.map((frequency, index) => H * frequency * (dceOccupation[index] ?? 0)));
  const coarseFrequency = frequencies.filter((_, index) => index % 2 === 0);
  const coarseOccupation = dceOccupation.filter((_, index) => index % 2 === 0);
  const coarseRate = trapezoid(coarseFrequency, coarseOccupation);
  const integrationError = Math.abs(photonRate - coarseRate) / Math.max(photonRate, Number.MIN_VALUE);
  const detuningHz = 10 ** state.scientific.frequencyLog10 - driveHz / 2;
  const detuningOmega = TWO_PI * detuningHz;
  const support = Math.max(0, 1 - 4 * (detuningOmega / driveOmega) ** 2);
  const theta = state.scientific.squeezingAngle * PI / 180;
  const squeezingNoise = Math.max(1e-6, 1 - 2 * epsilon * Math.sin(2 * theta) * Math.sqrt(support));
  const squeezingDb = 10 * Math.log10(squeezingNoise);
  const pairCorrelation = Math.sqrt(support);
  const g2Zero = epsilon > 0 ? 2 + 1 / (epsilon * epsilon) : Number.POSITIVE_INFINITY;
  const base = baseFrame(state, animationTimeSeconds, Math.max(1, driveHz / 2));
  const random = mulberry32(state.scientific.randomSeed + 2011);
  const detectorScale = Math.sqrt(Math.max(photonRate, 1e-12));
  const detectorSeries: PlotSeries[] = [{
    name: 'DCE photon counts',
    values: Array.from({ length: 160 }, (_, index) => {
      const time = index / 159;
      const envelope = 0.7 + 0.3 * Math.sin(TWO_PI * time * 3 + base.phase);
      return { x: index, y: Math.max(0, photonRate * envelope + gaussian(random) * detectorScale * 0.22) };
    }),
    xUnit: 'sample',
    yUnit: 'photons s^-1'
  }];
  const detunings = linspace(-driveHz / 2, driveHz / 2, 161);
  const correlationSeries: PlotSeries[] = [{
    name: 'Signal-idler pair correlation envelope',
    values: detunings.map((delta) => ({
      x: delta / 1e9,
      y: Math.sqrt(Math.max(0, 1 - 4 * (delta / driveHz) ** 2))
    })),
    xUnit: 'GHz detuning',
    yUnit: 'normalized'
  }];
  const thermalSpectrum: PlotSeries = {
    name: 'Thermal input',
    values: frequencies.map((frequency, index) => ({ x: frequency, y: thermalDirect[index] ?? 0 })),
    xUnit: 'Hz',
    yUnit: 'spectral occupation'
  };
  const dceSpectrum: PlotSeries = {
    name: 'DCE contribution',
    values: frequencies.map((frequency, index) => ({ x: frequency, y: dceOccupation[index] ?? 0 })),
    xUnit: 'Hz',
    yUnit: 'spectral occupation'
  };
  const outputSpectrum: PlotSeries = {
    name: 'Total output',
    values: frequencies.map((frequency, index) => ({ x: frequency, y: outputOccupation[index] ?? 0 })),
    xUnit: 'Hz',
    yUnit: 'spectral occupation'
  };
  const rEquivalent = Math.max(0, -0.5 * Math.log(squeezingNoise));
  const quadrature = quadratureCloud(
    state.scientific.randomSeed + 211,
    760,
    0.5 * Math.exp(-2 * rEquivalent),
    0.5 * Math.exp(2 * rEquivalent),
    state.scientific.squeezingAngle
  );
  const fieldSlice = replaceFieldSlice(base, (x, y) => {
    const travelling = Math.sin(PI * (x + 1) * 3 - base.phase * 2) * Math.cos(PI * y * 1.5);
    const pair = Math.sin(PI * (1 - x) * 3 - base.phase * 2) * Math.cos(PI * y * 1.5);
    return (travelling + pair) * (0.45 + 4 * epsilon);
  });
  const metrics = [
    metric('Drive frequency', driveHz, 'Hz', 'SQUID boundary-modulation frequency.'),
    metric('Effective length', effectiveLength, 'm'),
    metric('Length modulation', deltaLength, 'm'),
    metric('Effective velocity / v', velocityRatio, 'dimensionless'),
    metric('Perturbative epsilon', epsilon, 'dimensionless', 'Equation (22) small parameter; the perturbative model requires epsilon much less than one.'),
    metric('DCE photon rate', photonRate, 'photons/s'),
    metric('Pair rate', photonRate / 2, 'pairs/s'),
    metric('DCE radiated power', radiatedPower, 'W'),
    metric('Thermal photon rate', thermalRate, 'photons/s'),
    metric('Minimum quadrature noise', squeezingNoise, 'vacuum=1'),
    metric('Quadrature change', squeezingDb, 'dB'),
    metric('Pair-correlation envelope', pairCorrelation, 'dimensionless'),
    metric('g2(0), ideal perturbative', Number.isFinite(g2Zero) ? g2Zero : 0, 'dimensionless')
  ];
  return {
    ...base,
    warning: epsilon < 0.1
      ? 'Benchmark-validated Phase 7 research-release candidate adapter: perturbative open-waveguide SQUID dynamical Casimir model. The spectral pair-production term follows Johansson et al. (2010); full cryogenic-chain calibration and the complete 2011 apparatus transfer function are not reconstructed.'
      : 'Outside recommended perturbative range: epsilon is not much smaller than one. Results remain deterministic but should be treated as an extrapolation rather than a validated perturbative calculation.',
    modelId: 'dce-open-waveguide-v1',
    modelVersion: '1.0.0',
    adapterId: '2010s',
    frequencyBand: {
      centerHz: driveHz / 2,
      bandwidthHz: driveHz,
      lowerHz: 0,
      upperHz: driveHz,
      shape: 'rectangular'
    },
    displayAmplitude: clamp(0.9 + epsilon * 18, 0.75, 4.2),
    adapterMetrics: metrics,
    equations: [
      {
        id: 'dce-scattering',
        label: 'Perturbative boundary-mixing amplitude',
        html: '<i>S</i>(ω′,ω″)=i(δL<sub>eff</sub>/v)√(ω′ω″)',
        plain: 'S(omega1,omega2)=i*(delta_L_eff/v)*sqrt(omega1*omega2)',
        sourceIds: ['johansson-2010']
      },
      {
        id: 'dce-flux',
        label: 'Zero-temperature DCE spectral occupation',
        html: '<i>n</i><sub>DCE</sub>(ω)=(δL<sub>eff</sub>/v)²ω(ω<sub>d</sub>−ω)Θ(ω<sub>d</sub>−ω)',
        plain: 'n_DCE(omega)=(delta_L_eff/v)^2*omega*(omega_d-omega)*Theta(omega_d-omega)',
        sourceIds: ['johansson-2010']
      },
      {
        id: 'dce-squeezing',
        label: 'Leading-order quadrature squeezing spectrum',
        html: '<i>S</i><sub>X</sub><sup>θ</sup>(Δω)≈1−2ε sin(2θ)√[1−4(Δω/ω<sub>d</sub>)²]',
        plain: 'S_X_theta(delta_omega)=1-2*epsilon*sin(2*theta)*sqrt(1-4*(delta_omega/omega_d)^2)',
        sourceIds: ['johansson-2010']
      }
    ],
    plotSemantics: {
      spectrumTitle: 'Photon Spectrum',
      spectrumSubtitle: 'n_out(f)',
      quadratureTitle: 'Two-Mode Squeezing',
      correlationTitle: 'Pair Correlation',
      detectorTitle: 'Photon Rate',
      sliceTitle: 'Waveguide Field'
    },
    renderingMapping: 'Counterpropagating microwave field waves and paired emission are mapped into the common Three.js field volume. The plotted photon spectrum and pair rate are authoritative; glowing points are an explanatory encoding, not literal photons in the apparatus.',
    validation: {
      status: epsilon < 0.1 ? 'benchmark-validated' : 'qualified-extension',
      scope: 'Perturbative 1+1-dimensional open coplanar waveguide terminated by a harmonically flux-modulated SQUID, using the leading-order input-output result of Johansson et al. (2010).',
      relativeIntegrationError: integrationError,
      correlationNormalizationError: 0,
      deterministic: true,
      assumptions: [
        'Linear lossless coplanar waveguide with a single effective time-dependent boundary.',
        'Weak harmonic modulation and epsilon much less than one.',
        'Phase velocity is constant and dispersion is neglected.',
        'The SQUID remains below its plasma frequency and is represented through an effective electrical length.',
        'Receiver gain, cable loss, amplifier noise, finite acquisition filters, and pump leakage are not included.',
        'The integrated photon rate uses Gamma=integral df n_DCE(2*pi*f), consistent with continuum normalization.'
      ],
      sourceIds: ['johansson-2009', 'johansson-2010', 'wilson-2011', 'codata-2022']
    },
    spectrum: [outputSpectrum, dceSpectrum, thermalSpectrum],
    quadrature,
    correlationSeries,
    detectorSeries,
    fieldSlice
  };
};

export const calculateHomodyneFrame = (state: Readonly<AppState>, animationTimeSeconds: number): ScientificFrame => {
  const centerHz = 10 ** state.scientific.frequencyLog10;
  const quality = clamp(state.scientific.qualityFactor, 1, 1e7);
  const linewidthHz = centerHz / quality;
  const r = squeezingParameter(state.scientific.squeezingDb);
  const occupation = thermalOccupation(centerHz, state.scientific.temperature);
  const baseVariance = occupation + 0.5;
  const minimumVariance = baseVariance * Math.exp(-2 * r);
  const maximumVariance = baseVariance * Math.exp(2 * r);
  const efficiency = clamp(state.scientific.detectorEfficiency, 0, 1);
  const detectedMinimumVariance = efficiency * minimumVariance + (1 - efficiency) * 0.5;
  const detectedMaximumVariance = efficiency * maximumVariance + (1 - efficiency) * 0.5;
  const theta = state.scientific.squeezingAngle * PI / 180;
  const idealSelectedVariance = minimumVariance * Math.cos(theta) ** 2 + maximumVariance * Math.sin(theta) ** 2;
  const measuredVariance = efficiency * idealSelectedVariance + (1 - efficiency) * 0.5;
  const normalizedNoise = measuredVariance / 0.5;
  const measuredNoiseDb = 10 * Math.log10(Math.max(normalizedNoise, 1e-15));
  const meanPhotons = occupation + Math.sinh(r) ** 2;
  const span = Math.max(linewidthHz * 8, 10 ** state.scientific.detectorBandwidthLog10);
  const frequencies = linspace(centerHz - span / 2, centerHz + span / 2, 241);
  const squeezedSpectrum = frequencies.map((frequency) => {
    const response = normalizedLorentzian(frequency, centerHz, linewidthHz);
    return 1 + (Math.exp(-2 * r) - 1) * response;
  });
  const antiSpectrum = frequencies.map((frequency) => {
    const response = normalizedLorentzian(frequency, centerHz, linewidthHz);
    return 1 + (Math.exp(2 * r) - 1) * response;
  });
  const correlationTimes = linspace(-8 / Math.max(linewidthHz, 1), 8 / Math.max(linewidthHz, 1), 161);
  const correlationSeries: PlotSeries[] = [{
    name: 'Cavity quadrature correlation',
    values: correlationTimes.map((time) => ({ x: time * 1e9, y: Math.exp(-PI * linewidthHz * Math.abs(time)) * Math.cos(TWO_PI * centerHz * time) })),
    xUnit: 'ns',
    yUnit: 'normalized'
  }];
  const quadrature = quadratureCloud(state.scientific.randomSeed + 1983, 800, detectedMinimumVariance, detectedMaximumVariance, state.scientific.squeezingAngle);
  const random = mulberry32(state.scientific.randomSeed + 2000);
  const detectorSeries: PlotSeries[] = [{
    name: 'Balanced homodyne samples',
    values: Array.from({ length: 180 }, (_, index) => ({ x: index, y: gaussian(random) * Math.sqrt(measuredVariance) })),
    xUnit: 'sample',
    yUnit: 'vacuum quadrature units'
  }];
  const base = baseFrame(state, animationTimeSeconds, centerHz);
  const fieldSlice = replaceFieldSlice(base, (x, y) => {
    const cavity = Math.sin(PI * (x + 1) * 2) * Math.cos(PI * y);
    const localOscillator = 0.28 * Math.sin(PI * (x - y) * 5 + base.phase * 3);
    return (cavity + localOscillator) * Math.sqrt(Math.max(normalizedNoise, 0.1));
  });
  return {
    ...base,
    warning: 'Quantitative Gaussian-state Phase 7 research-release candidate teaching adapter: single-mode squeezed vacuum, Lorentzian cavity response, and ideal balanced-homodyne sampling. Optical loss, detector dark noise, electronic response, and a specific laboratory transfer function are not reconstructed.',
    modelId: 'squeezed-homodyne-v1',
    modelVersion: '1.0.0',
    adapterId: '1990s-2000s',
    displayAmplitude: clamp(0.8 + r * 0.9, 0.7, 3.8),
    adapterMetrics: [
      metric('Cavity centre', centerHz, 'Hz'),
      metric('Cavity linewidth', linewidthHz, 'Hz'),
      metric('Quality factor', quality, 'dimensionless'),
      metric('Squeezing parameter r', r, 'dimensionless'),
      metric('Minimum variance', minimumVariance, 'quadrature units'),
      metric('Maximum variance', maximumVariance, 'quadrature units'),
      metric('Detection efficiency', efficiency, 'dimensionless'),
      metric('Detected minimum variance', detectedMinimumVariance, 'quadrature units'),
      metric('Detected maximum variance', detectedMaximumVariance, 'quadrature units'),
      metric('Selected ideal variance', idealSelectedVariance, 'quadrature units'),
      metric('Selected detected variance', measuredVariance, 'quadrature units'),
      metric('Selected detected noise', measuredNoiseDb, 'dB relative to vacuum'),
      metric('Mean photon occupation', meanPhotons, 'photons/mode')
    ],
    equations: [
      {
        id: 'squeezed-state',
        label: 'Single-mode squeezed vacuum',
        html: '|ψ⟩=<i>S</i>(r,φ)|0⟩, &nbsp; <i>S</i>=exp[(r/2)(e<sup>−iφ</sup>a²−e<sup>iφ</sup>a†²)]',
        plain: '|psi>=S(r,phi)|0>; S=exp[(r/2)(e^-i phi a^2-e^i phi a_dagger^2)]',
        sourceIds: ['caves-1981', 'walls-1983']
      },
      {
        id: 'homodyne-variance',
        label: 'Rotated quadrature variance',
        html: '<i>V</i><sub>θ</sub>=(n̄+½)[e<sup>−2r</sup>cos²(θ−φ)+e<sup>2r</sup>sin²(θ−φ)]',
        plain: 'V_theta=(nbar+1/2)[exp(-2r)cos^2(theta-phi)+exp(2r)sin^2(theta-phi)]',
        sourceIds: ['caves-1981', 'walls-1983']
      }
    ],
    plotSemantics: {
      spectrumTitle: 'Noise Spectrum',
      spectrumSubtitle: 'vacuum=1',
      quadratureTitle: 'Homodyne Phase Space',
      correlationTitle: 'Cavity Correlation',
      detectorTitle: 'Homodyne Signal',
      sliceTitle: 'Cavity Mode'
    },
    renderingMapping: 'The cavity standing wave and local-oscillator phase are mapped to the shared field display. The quadrature covariance and homodyne samples are authoritative within the declared Gaussian idealization.',
    validation: {
      status: 'qualified-extension',
      scope: 'Ideal single-mode Gaussian squeezed state with a passive Lorentzian cavity envelope and ideal balanced-homodyne detection.',
      relativeIntegrationError: 0,
      correlationNormalizationError: 0,
      deterministic: true,
      assumptions: [
        'Single optical or microwave mode with Gaussian statistics.',
        'Squeezing is uniform over the displayed cavity linewidth.',
        'Local oscillator is classical and phase stable.',
        'Detection inefficiency is modeled as a beam-splitter loss channel that mixes the signal with vacuum noise.',
        'No frequency-dependent optical loss, electronics noise, or nonlinear-crystal transfer function.'
      ],
      sourceIds: ['caves-1981', 'walls-1983', 'schumaker-caves-1985', 'codata-2022']
    },
    spectrum: [
      { name: 'Squeezed quadrature', values: frequencies.map((frequency, index) => ({ x: frequency, y: squeezedSpectrum[index] ?? 1 })), xUnit: 'Hz', yUnit: 'vacuum=1' },
      { name: 'Anti-squeezed quadrature', values: frequencies.map((frequency, index) => ({ x: frequency, y: antiSpectrum[index] ?? 1 })), xUnit: 'Hz', yUnit: 'vacuum=1' },
      { name: 'Shot-noise level', values: frequencies.map((frequency) => ({ x: frequency, y: 1 })), xUnit: 'Hz', yUnit: 'vacuum=1' }
    ],
    quadrature,
    correlationSeries,
    detectorSeries,
    fieldSlice
  };
};

export const calculateDynamicBoundaryFrame = (state: Readonly<AppState>, animationTimeSeconds: number): ScientificFrame => {
  const cavityHz = 10 ** state.scientific.cavityResonanceLog10;
  const driveHz = 10 ** state.scientific.modulationFrequencyLog10;
  const omega0 = TWO_PI * cavityHz;
  const driveOmega = TWO_PI * driveHz;
  const modulationDepth = clamp(state.scientific.modulationAmplitude, 0, 0.2);
  const quality = clamp(state.scientific.qualityFactor, 1, 1e8);
  const couplingRate = modulationDepth * omega0 / 4;
  const amplitudeLossRate = omega0 / (2 * quality);
  const detuning = driveOmega - 2 * omega0;
  const coherentGain = Math.sqrt(Math.max(0, couplingRate ** 2 - (detuning / 2) ** 2));
  const netGain = coherentGain - amplitudeLossRate;
  const requestedTime = 10 ** state.scientific.integrationTimeLog10;
  const stableTimeLimit = couplingRate > 0 ? 12 / couplingRate : requestedTime;
  const interactionTime = Math.min(requestedTime, stableTimeLimit);
  const squeezeAmount = Math.max(0, coherentGain * interactionTime);
  const lossFactor = Math.exp(-2 * amplitudeLossRate * interactionTime);
  const photonNumber = Math.sinh(squeezeAmount) ** 2 * lossFactor;
  const thresholdRatio = couplingRate / Math.max(amplitudeLossRate, Number.MIN_VALUE);
  const detuningSpan = Math.max(cavityHz / quality * 12, cavityHz * 0.15);
  const detunings = linspace(-detuningSpan, detuningSpan, 201);
  const occupancy = detunings.map((deltaHz) => {
    const localDetuning = TWO_PI * (driveHz - 2 * (cavityHz + deltaHz));
    const localGain = Math.sqrt(Math.max(0, couplingRate ** 2 - (localDetuning / 2) ** 2));
    const localR = Math.max(0, localGain * interactionTime);
    return Math.sinh(Math.min(localR, 12)) ** 2 * lossFactor;
  });
  const timePoints = linspace(0, interactionTime, 161);
  const growthSeries: PlotSeries[] = [{
    name: 'Mode occupation',
    values: timePoints.map((time) => ({ x: time * 1e9, y: Math.sinh(Math.min(coherentGain * time, 12)) ** 2 * Math.exp(-2 * amplitudeLossRate * time) })),
    xUnit: 'ns',
    yUnit: 'photons'
  }];
  const base = baseFrame(state, animationTimeSeconds, cavityHz);
  const varianceX = 0.5 * Math.exp(-2 * squeezeAmount);
  const varianceP = 0.5 * Math.exp(2 * squeezeAmount);
  const quadrature = quadratureCloud(state.scientific.randomSeed + 1970, 760, varianceX, varianceP, state.scientific.squeezingAngle);
  const random = mulberry32(state.scientific.randomSeed + 1980);
  const detectorSeries = [{
    name: 'Predicted cavity counts',
    values: Array.from({ length: 160 }, (_, index) => ({ x: index, y: Math.max(0, photonNumber + gaussian(random) * Math.sqrt(Math.max(photonNumber, 0.01))) })),
    xUnit: 'sample',
    yUnit: 'photons'
  }];
  const fieldSlice = replaceFieldSlice(base, (x, y) => {
    const boundaryPhase = modulationDepth * Math.sin(base.phase * 2);
    const shiftedX = clamp(x / Math.max(0.5, 1 + boundaryPhase), -1, 1);
    return Math.sin(PI * (shiftedX + 1) / 2) * Math.cos(PI * y * 0.5) * Math.sqrt(2 * photonNumber + 1);
  });
  return {
    ...base,
    warning: 'Historical Phase 7 research-release candidate reconstruction: resonantly modulated single-mode cavity under the rotating-wave effective Hamiltonian. This is a quantitative pedagogical model of parametric vacuum amplification, not Moore’s exact arbitrary-trajectory solution.',
    modelId: 'dynamic-boundary-parametric-v1',
    modelVersion: '1.0.0',
    adapterId: '1970s-1980s',
    displayAmplitude: clamp(0.8 + Math.log10(1 + photonNumber) * 0.6, 0.7, 4.2),
    adapterMetrics: [
      metric('Cavity frequency', cavityHz, 'Hz'),
      metric('Boundary drive', driveHz, 'Hz'),
      metric('Drive detuning', detuning / TWO_PI, 'Hz'),
      metric('Parametric coupling g', couplingRate, 'rad/s'),
      metric('Amplitude loss rate', amplitudeLossRate, 'rad/s'),
      metric('Net gain rate', netGain, 'rad/s'),
      metric('Threshold ratio g/(kappa/2)', thresholdRatio, 'dimensionless'),
      metric('Interaction time', interactionTime, 's'),
      metric('Squeezing amount', squeezeAmount, 'dimensionless'),
      metric('Mode occupation', photonNumber, 'photons')
    ],
    equations: [
      {
        id: 'dynamic-boundary-hamiltonian',
        label: 'Resonant single-mode effective Hamiltonian',
        html: '<i>H</i><sub>int</sub>=iℏg(a†²−a²)/2, &nbsp; g≈εω<sub>0</sub>/4',
        plain: 'H_int=i*hbar*g*(a_dagger^2-a^2)/2; g approximately epsilon*omega_0/4',
        sourceIds: ['moore-1970', 'law-1994']
      },
      {
        id: 'dynamic-boundary-occupation',
        label: 'Loss-qualified resonant occupation',
        html: 'N(t)=sinh²(g<sub>eff</sub>t)e<sup>−2κt</sup>',
        plain: 'N(t)=sinh^2(g_eff*t)*exp(-2*kappa*t)',
        sourceIds: ['law-1994']
      }
    ],
    plotSemantics: {
      spectrumTitle: 'Mode Spectrum',
      spectrumSubtitle: 'N(delta f)',
      quadratureTitle: 'Parametric Quadratures',
      correlationTitle: 'Growth in Time',
      detectorTitle: 'Mode Counts',
      sliceTitle: 'Moving-Boundary Mode'
    },
    renderingMapping: 'A standing cavity mode is warped by the selected boundary modulation. The authoritative result is the effective-Hamiltonian mode occupation; apparent wall motion is visually magnified.',
    validation: {
      status: 'historical-reconstruction',
      scope: 'Single-mode resonant parametric approximation to a variable-length cavity, with a phenomenological quality factor and rotating-wave approximation.',
      relativeIntegrationError: 0,
      correlationNormalizationError: 0,
      deterministic: true,
      assumptions: [
        'One-dimensional cavity and one dominant mode.',
        'Small sinusoidal fractional modulation.',
        'Rotating-wave approximation near drive frequency 2*omega0.',
        'Constant phenomenological quality factor.',
        'Back-reaction and multimode coupling are neglected.',
        'Interaction time is capped before numerical overflow; the cap is reported through the active value.'
      ],
      sourceIds: ['moore-1970', 'law-1994', 'codata-2022']
    },
    spectrum: [{ name: 'Mode occupation versus detuning', values: detunings.map((delta, index) => ({ x: delta / 1e6, y: occupancy[index] ?? 0 })), xUnit: 'MHz', yUnit: 'photons' }],
    quadrature,
    correlationSeries: growthSeries,
    detectorSeries,
    fieldSlice
  };
};

export const calculateStaticCasimirFrame = (state: Readonly<AppState>, animationTimeSeconds: number): ScientificFrame => {
  const separation = 10 ** state.scientific.boundarySeparationLog10;
  const geometry = state.scientific.boundaryGeometry;
  const radius = Math.max(separation * 5, 10 ** state.scientific.spatialLog10);
  const polarizabilityVolume = 10 ** state.scientific.polarizabilityVolumeLog10;
  const energyPerArea = -(PI ** 2) * HBAR * C / (720 * separation ** 3);
  const pressure = -(PI ** 2) * HBAR * C / (240 * separation ** 4);
  const spherePlaneForce = -(PI ** 3) * HBAR * C * radius / (360 * separation ** 3);
  const casimirPolderPotential = -3 * HBAR * C * polarizabilityVolume / (8 * PI * separation ** 4);
  const fundamentalHz = C / (2 * separation);
  const modeCount = clamp(Math.round(state.scientific.numericalResolution / 4), 8, 40);
  const modes = Array.from({ length: modeCount }, (_, index) => (index + 1) * fundamentalHz);
  const cutoffHz = 10 ** state.scientific.frequencyLog10;
  const modeSpectrum: PlotSeries[] = [{
    name: 'Allowed normal modes',
    values: modes.flatMap((frequency) => [
      { x: frequency, y: 0 },
      { x: frequency, y: frequency <= cutoffHz ? 1 : 0.2 },
      { x: frequency * 1.0000001, y: 0 }
    ]),
    xUnit: 'Hz',
    yUnit: 'relative mode weight'
  }];
  const separations = linspace(separation / 2, separation * 3, 161);
  const forceSeries: PlotSeries[] = [{
    name: geometry === 'sphere' ? 'Sphere-plane PFA force' : geometry === 'custom' ? 'Retarded atom-surface potential' : 'Parallel-plate pressure',
    values: separations.map((distance) => ({
      x: distance * 1e9,
      y: geometry === 'sphere'
        ? -(PI ** 3) * HBAR * C * radius / (360 * distance ** 3)
        : geometry === 'custom'
          ? -3 * HBAR * C * polarizabilityVolume / (8 * PI * distance ** 4)
          : -(PI ** 2) * HBAR * C / (240 * distance ** 4)
    })),
    xUnit: 'nm',
    yUnit: geometry === 'sphere' ? 'N' : geometry === 'custom' ? 'J' : 'Pa'
  }];
  const base = baseFrame(state, animationTimeSeconds, Math.min(cutoffHz, Math.max(fundamentalHz, 1)));
  const quadrature = quadratureCloud(state.scientific.randomSeed + 1958, 760, 0.5, 0.5, state.scientific.squeezingAngle);
  const detectorScale = Math.abs(geometry === 'sphere' ? spherePlaneForce : geometry === 'custom' ? casimirPolderPotential : pressure);
  const detectorSeries = noisySeries(state.scientific.randomSeed + 1960, 160, Math.max(detectorScale * 0.025, 1e-30), geometry === 'sphere' ? spherePlaneForce : geometry === 'custom' ? casimirPolderPotential : pressure);
  const fieldSlice = replaceFieldSlice(base, (x, y) => {
    const modeIndex = Math.max(1, Math.min(6, Math.round(cutoffHz / Math.max(fundamentalHz, 1))));
    const inside = Math.abs(x) < 0.72;
    return (inside ? Math.sin(modeIndex * PI * (x + 0.72) / 1.44) : 0.18 * Math.sin(5 * PI * x)) * Math.cos(PI * y) * Math.cos(base.phase);
  });
  const selectedMetric = geometry === 'sphere'
    ? metric('Sphere-plane PFA force', spherePlaneForce, 'N')
    : geometry === 'custom'
      ? metric('Retarded atom-surface potential', casimirPolderPotential, 'J')
      : metric('Parallel-plate pressure', pressure, 'Pa');
  const status = geometry === 'plates' ? 'benchmark-validated' : 'qualified-extension';
  return {
    ...base,
    warning: geometry === 'plates'
      ? 'Benchmark-validated Phase 7 research-release candidate adapter: ideal, perfectly conducting, zero-temperature parallel-plate Casimir energy and pressure. The field image is a mode-structure teaching map, not a local stress-tensor calculation.'
      : 'Qualified Phase 7 research-release candidate static-boundary adapter: sphere-plane uses the proximity-force approximation and the atom-surface option uses the retarded perfect-conductor Casimir-Polder asymptote.',
    modelId: 'static-casimir-ideal-v1',
    modelVersion: '1.0.0',
    adapterId: '1950s-1960s',
    displayAmplitude: clamp(0.9 + Math.log10(1 + Math.abs(pressure)) * 0.18, 0.7, 3.8),
    observables: { ...base.observables, casimirEnergyPerAreaJPerM2: energyPerArea, casimirPressurePa: pressure },
    adapterMetrics: [
      metric('Boundary separation', separation, 'm'),
      metric('Fundamental cavity mode', fundamentalHz, 'Hz'),
      metric('Ideal energy per area', energyPerArea, 'J/m2'),
      metric('Ideal plate pressure', pressure, 'Pa'),
      metric('Sphere radius / teaching scale', radius, 'm'),
      metric('Sphere-plane PFA force', spherePlaneForce, 'N'),
      metric('Polarizability volume', polarizabilityVolume, 'm3'),
      metric('Retarded atom-surface potential', casimirPolderPotential, 'J'),
      selectedMetric
    ],
    equations: [
      {
        id: 'casimir-energy',
        label: 'Ideal parallel-plate Casimir energy',
        html: '<i>E</i>/<i>A</i>=−π²ℏc/(720d³), &nbsp; <i>P</i>=−π²ℏc/(240d⁴)',
        plain: 'E/A=-pi^2*hbar*c/(720*d^3); P=-pi^2*hbar*c/(240*d^4)',
        sourceIds: ['casimir-1948']
      },
      {
        id: 'sphere-plane-pfa',
        label: 'Sphere-plane proximity-force approximation',
        html: '<i>F</i><sub>PFA</sub>≈−π³ℏcR/(360d³)',
        plain: 'F_PFA=-pi^3*hbar*c*R/(360*d^3)',
        sourceIds: ['casimir-1948', 'bordag-2009']
      },
      {
        id: 'casimir-polder-wall',
        label: 'Retarded atom-perfect-conductor potential',
        html: '<i>U</i>(z)=−3ℏcα<sub>v</sub>/(8πz⁴), &nbsp; α<sub>v</sub>=α(0)/(4πε₀)',
        plain: 'U(z)=-3*hbar*c*alpha_volume/(8*pi*z^4)',
        sourceIds: ['casimir-polder-1948']
      }
    ],
    plotSemantics: {
      spectrumTitle: 'Mode Density',
      spectrumSubtitle: 'allowed modes',
      quadratureTitle: 'Vacuum Quadratures',
      correlationTitle: 'Force vs Separation',
      detectorTitle: 'Force Readout',
      sliceTitle: 'Boundary Modes'
    },
    renderingMapping: 'The plates, sphere, or atom-surface geometry constrains a finite set of displayed standing modes. Analytic energy, pressure, force, and potential values are authoritative; local colors do not represent a computed renormalized stress tensor.',
    validation: {
      status,
      scope: geometry === 'plates'
        ? 'Ideal zero-temperature, perfectly conducting parallel plates in vacuum.'
        : geometry === 'sphere'
          ? 'Ideal sphere-plane force in the proximity-force approximation for d much smaller than R.'
          : 'Retarded atom-perfect-conductor asymptote using a static isotropic polarizability volume.',
      relativeIntegrationError: 0,
      correlationNormalizationError: 0,
      deterministic: true,
      assumptions: [
        'Zero temperature and perfect conductivity for the analytic Casimir expressions.',
        'No roughness, finite thickness, patch potentials, dielectric dispersion, or finite-temperature Lifshitz correction.',
        geometry === 'sphere' ? 'Proximity-force approximation requires separation much smaller than sphere radius.' : 'Parallel-plane geometry unless another explicitly qualified option is selected.',
        geometry === 'custom' ? 'Casimir-Polder expression is the retarded asymptotic perfect-conductor limit.' : 'Mode visualization uses a finite cutoff and is not used to numerically derive the renormalized force.'
      ],
      sourceIds: geometry === 'custom'
        ? ['casimir-polder-1948', 'codata-2022']
        : ['casimir-1948', 'bordag-2009', 'codata-2022']
    },
    spectrum: modeSpectrum,
    quadrature,
    correlationSeries: forceSeries,
    detectorSeries,
    fieldSlice
  };
};

export const calculateLambShiftFrame = (state: Readonly<AppState>, animationTimeSeconds: number): ScientificFrame => {
  const measuredShiftHz = 1057.8e6;
  const atomicNumber = clamp(Math.round(state.scientific.atomicNumber), 1, 20);
  const cutoffRatio = clamp(state.scientific.cutoffRatio, 1.01, 1e8);
  const n = 2;
  const coefficientHz = (4 / (3 * PI)) * ALPHA ** 5 * atomicNumber ** 4 * ME * C ** 2 / (n ** 3 * H);
  const leadingLogHz = coefficientHz * Math.log(cutoffRatio);
  const shiftEnergyJ = H * measuredShiftHz;
  const modelEnergyJ = H * leadingLogHz;
  const relativeError = Math.abs(leadingLogHz - measuredShiftHz) / measuredShiftHz;
  const lineWidthHz = Math.max(1e6, 10 ** state.scientific.detectorBandwidthLog10);
  const detunings = linspace(-2.2e9, 2.2e9, 241);
  const shiftedLine = detunings.map((detuning) => normalizedLorentzian(detuning, measuredShiftHz, lineWidthHz));
  const diracLine = detunings.map((detuning) => normalizedLorentzian(detuning, 0, lineWidthHz));
  const base = baseFrame(state, animationTimeSeconds, measuredShiftHz);
  const random = mulberry32(state.scientific.randomSeed + 1947);
  const counts = detunings.map((detuning, index) => {
    const signal = 1800 * (shiftedLine[index] ?? 0) + 300 * (diracLine[index] ?? 0);
    return Math.max(0, signal + gaussian(random) * Math.sqrt(Math.max(signal, 1)));
  });
  const detectorSeries: PlotSeries[] = [{
    name: 'Microwave spectroscopy counts',
    values: detunings.map((detuning, index) => ({ x: detuning / 1e6, y: counts[index] ?? 0 })),
    xUnit: 'MHz relative frequency',
    yUnit: 'counts'
  }];
  const correlationSeries: PlotSeries[] = [{
    name: 'Observed transition line',
    values: detunings.map((detuning, index) => ({ x: detuning / 1e6, y: shiftedLine[index] ?? 0 })),
    xUnit: 'MHz',
    yUnit: 'normalized response'
  }];
  const quadrature = Array.from({ length: 760 }, (_, index) => {
    const level = index % 2 === 0 ? -0.7 : 0.7;
    return { x: gaussian(random) * 0.28 + level, y: gaussian(random) * 0.14 + (index % 4 < 2 ? -0.6 : 0.6) };
  });
  const fieldSlice = replaceFieldSlice(base, (x, y) => {
    const radius = Math.sqrt(x * x + y * y);
    const twoS = (1 - radius * 2) * Math.exp(-radius * 2.4);
    const twoP = x * Math.exp(-radius * 2.8);
    return twoS + 0.45 * twoP * Math.cos(base.phase);
  });
  return {
    ...base,
    warning: 'Historical Phase 7 research-release candidate foundations adapter: the measured Lamb-Retherford 2S1/2-2P1/2 separation is retained as an empirical benchmark. The adjustable theoretical value is an explicitly simplified leading-log hydrogenic teaching estimate, not a modern bound-state QED calculation.',
    modelId: 'lamb-shift-foundations-v1',
    modelVersion: '1.0.0',
    adapterId: '1940s',
    displayAmplitude: 1.15,
    adapterMetrics: [
      metric('Observed Lamb shift', measuredShiftHz, 'Hz'),
      metric('Observed energy shift', shiftEnergyJ, 'J'),
      metric('Leading-log estimate', leadingLogHz, 'Hz'),
      metric('Leading-log energy', modelEnergyJ, 'J'),
      metric('Estimate relative error', relativeError, 'dimensionless'),
      metric('Atomic number Z', atomicNumber, 'dimensionless'),
      metric('Cutoff ratio', cutoffRatio, 'dimensionless'),
      metric('Equivalent free-space wavelength', C / measuredShiftHz, 'm')
    ],
    equations: [
      {
        id: 'lamb-observed',
        label: 'Measured 2S1/2-2P1/2 separation',
        html: 'Δ<i>E</i><sub>Lamb</sub>=hΔν, &nbsp; Δν<sub>obs</sub>≈1057.8 MHz',
        plain: 'Delta_E_Lamb=h*Delta_nu; Delta_nu_observed approximately 1057.8 MHz',
        sourceIds: ['lamb-retherford-1947']
      },
      {
        id: 'bethe-leading-log',
        label: 'Simplified hydrogenic leading-log teaching estimate',
        html: 'Δν<sub>LL</sub>=(4/3π)α⁵Z⁴(m<sub>e</sub>c²/h)n<sup>−3</sup>lnΛ',
        plain: 'Delta_nu_LL=(4/(3*pi))*alpha^5*Z^4*(m_e*c^2/h)*n^-3*ln(Lambda)',
        sourceIds: ['bethe-1947']
      }
    ],
    plotSemantics: {
      spectrumTitle: 'Atomic Spectrum',
      spectrumSubtitle: '2S-2P lines',
      quadratureTitle: 'Energy-Level Map',
      correlationTitle: 'Transition Line',
      detectorTitle: 'Spectrometer Counts',
      sliceTitle: 'Orbital Teaching View'
    },
    renderingMapping: 'The central field view combines hydrogenic 2S/2P teaching shapes with microwave-transition activity. The measured frequency and calculated leading-log estimate are the numerical content; the orbital image is qualitative.',
    validation: {
      status: 'historical-reconstruction',
      scope: 'Empirical Lamb-Retherford transition benchmark plus a simplified leading-log hydrogenic self-energy teaching estimate.',
      relativeIntegrationError: relativeError,
      correlationNormalizationError: 0,
      deterministic: true,
      assumptions: [
        'Observed shift is fixed to the historical 1057.8 MHz benchmark used by the visualizer.',
        'The theoretical control is not a full relativistic bound-state QED calculation.',
        'The cutoff ratio is pedagogical and exposes the logarithmic sensitivity of the historical approximation.',
        'Fine structure, recoil, vacuum polarization, finite nuclear size, and higher-order radiative corrections are omitted.',
        'Displayed orbital density is qualitative and is not used to derive the shift.'
      ],
      sourceIds: ['lamb-retherford-1947', 'bethe-1947', 'welton-1948', 'codata-2022']
    },
    spectrum: [
      { name: 'Observed shifted line', values: detunings.map((detuning, index) => ({ x: detuning / 1e6, y: shiftedLine[index] ?? 0 })), xUnit: 'MHz', yUnit: 'normalized' },
      { name: 'Dirac-degenerate reference', values: detunings.map((detuning, index) => ({ x: detuning / 1e6, y: diracLine[index] ?? 0 })), xUnit: 'MHz', yUnit: 'normalized' }
    ],
    quadrature,
    correlationSeries,
    detectorSeries,
    fieldSlice
  };
};
